import React from 'react';
import Section from '../components/common/Section';
import Container from '../components/common/Container';

const Terms: React.FC = () => {
  return (
    <Section padding="lg">
      <Container size="md">
        <h1 className="text-4xl md:text-5xl font-black text-[var(--color-eel)] mb-8 text-center">
          Terms of Service
        </h1>

        <div className="prose prose-invert max-w-none space-y-8 text-[var(--color-wolf)]">
          <div>
            <p className="text-sm text-[var(--color-hare)] mb-8">
              Last Updated: November 2025
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">1. Agreement to Terms</h2>
            <p className="leading-relaxed">
              By accessing or using Lumen8 (the "Platform"), operated by Lumen8 Pty Ltd ABN [ABN Number] ("Company", "we", "us", or "our"),
              you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms,
              you may not access the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">2. Description of Service</h2>
            <p className="leading-relaxed mb-4">
              Lumen8 is an AI-powered adaptive learning platform that provides:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Curriculum-aligned educational content for K-12 students</li>
              <li>Real-time progress tracking and mastery assessment</li>
              <li>Adaptive learning paths based on individual performance</li>
              <li>Teacher dashboards and classroom management tools</li>
              <li>Parent portals for monitoring student progress</li>
              <li>Gamification features to enhance engagement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">3. User Accounts</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">3.1 Account Types</h3>
            <p className="leading-relaxed mb-4">
              The Platform offers different account types: Student, Teacher, Parent, School Administrator, and System Administrator.
              Each account type has specific permissions and access levels.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">3.2 Account Creation</h3>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Student accounts must be created by schools or parents/guardians</li>
              <li>Users must provide accurate and complete information</li>
              <li>Users must be authorized to create accounts on behalf of minors</li>
              <li>Schools are responsible for verifying parental consent where required</li>
            </ul>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">3.3 Account Security</h3>
            <p className="leading-relaxed">
              Users are responsible for maintaining the confidentiality of their account credentials and for all activities
              that occur under their account. Users must immediately notify us of any unauthorized use or security breach.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">4. Acceptable Use Policy</h2>
            <p className="leading-relaxed mb-4">Users agree not to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Use the Platform for any unlawful purpose or in violation of any regulations</li>
              <li>Share account credentials or allow unauthorized access</li>
              <li>Attempt to gain unauthorized access to any part of the Platform</li>
              <li>Interfere with or disrupt the Platform's operation</li>
              <li>Upload malicious code or harmful content</li>
              <li>Harass, bully, or intimidate other users</li>
              <li>Use automated systems to access the Platform without permission</li>
              <li>Misrepresent your identity or affiliation</li>
              <li>Collect or harvest user data without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">5. Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">5.1 Platform Content</h3>
            <p className="leading-relaxed mb-4">
              All content on the Platform, including text, graphics, logos, audio, video, and software, is the property
              of Lumen8 Pty Ltd or its licensors and is protected by Australian and international intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">5.2 User Content</h3>
            <p className="leading-relaxed mb-4">
              Users retain ownership of content they create but grant us a worldwide, non-exclusive, royalty-free license
              to use, modify, and display such content for Platform operation and improvement.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">5.3 Educational Use</h3>
            <p className="leading-relaxed">
              Schools and teachers may use Platform content for educational purposes within their institution but may not
              redistribute or commercialize such content without written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">6. Payment Terms</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">6.1 Subscription Fees</h3>
            <p className="leading-relaxed mb-4">
              Schools pay subscription fees based on the terms of their agreement with Lumen8 Pty Ltd.
              Contact us for details on subscription options.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">6.2 Payment Terms</h3>
            <ul className="list-disc list-inside mb-4 space-y-2">
              <li>Payment terms are specified in individual school agreements</li>
              <li>All fees are in Australian Dollars unless otherwise specified</li>
              <li>Refund policies are as specified in individual agreements</li>
            </ul>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">6.3 Taxes</h3>
            <p className="leading-relaxed">
              All fees are exclusive of taxes. Schools are responsible for all applicable taxes including GST.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">7. Privacy and Data Protection</h2>
            <p className="leading-relaxed">
              Our collection and use of personal information is governed by our Privacy Policy, which is incorporated
              into these Terms by reference. We comply with Australian privacy laws and educational data protection requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">8. Disclaimers and Limitations</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">8.1 Educational Disclaimer</h3>
            <p className="leading-relaxed mb-4">
              The Platform is an educational tool designed to supplement, not replace, traditional instruction. We do not
              guarantee specific educational outcomes or improvements in academic performance.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">8.2 Service Availability</h3>
            <p className="leading-relaxed mb-4">
              We strive for 99.9% uptime but do not guarantee uninterrupted or error-free service. We may suspend access
              for maintenance, updates, or security reasons.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">8.3 Limitation of Liability</h3>
            <p className="leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LUMEN8 PTY LTD SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY
              OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">9. Indemnification</h2>
            <p className="leading-relaxed">
              Users agree to indemnify and hold harmless Lumen8 Pty Ltd, its officers, directors, employees, and agents
              from any claims, damages, losses, or expenses arising from your use of the Platform or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">10. Termination</h2>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">10.1 Termination by Users</h3>
            <p className="leading-relaxed mb-4">
              Schools may terminate their subscription according to the terms of their agreement. Individual users may
              request account deletion through their school administrator.
            </p>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">10.2 Termination by Us</h3>
            <p className="leading-relaxed mb-4">
              We may terminate or suspend access immediately, without prior notice, for:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Violation of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Suspected fraudulent or illegal activity</li>
              <li>Request by law enforcement or court order</li>
            </ul>

            <h3 className="text-xl font-semibold text-[var(--color-eel)] mb-3">10.3 Effect of Termination</h3>
            <p className="leading-relaxed">
              Upon termination, user access will be disabled. Schools may request data export within 30 days of termination,
              after which data may be deleted.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">11. Modifications to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these Terms at any time. Material changes will be notified to school administrators
              at least 30 days before taking effect. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">12. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms are governed by the laws of New South Wales, Australia. Any disputes shall be subject to the
              exclusive jurisdiction of the courts of New South Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">13. Dispute Resolution</h2>
            <p className="leading-relaxed mb-4">
              Before initiating legal proceedings, parties agree to attempt good faith negotiation. If unresolved within
              30 days, disputes may be submitted to mediation under the Australian Commercial Disputes Centre rules.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">14. Severability</h2>
            <p className="leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in
              full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">15. Entire Agreement</h2>
            <p className="leading-relaxed">
              These Terms, together with our Privacy Policy and any school agreements, constitute the entire agreement
              between you and Lumen8 Pty Ltd regarding the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[var(--color-eel)] mb-4">16. Contact Information</h2>
            <p className="leading-relaxed mb-4">
              For questions about these Terms, please contact:
            </p>
            <div className="bg-[var(--color-swan)] p-6 rounded-lg">
              <p className="font-semibold text-[var(--color-eel)]">Legal Department</p>
              <p>Lumen8 Pty Ltd</p>
              <p>Email: legal@lumen8.it</p>
              <p>Website: www.lumen8.it</p>
              <p>Address: Sydney, NSW, Australia</p>
            </div>
          </section>

          <section className="mt-12 p-6 bg-[var(--color-swan)] rounded-lg">
            <p className="text-sm text-[var(--color-hare)]">
              By using Lumen8, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </Container>
    </Section>
  );
};

export default Terms;